import { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Rectangle,
} from 'recharts';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../../../components/ui/molecules/table';
import { ChartCard, ChartLegend } from '../components';
import { AllocationChartProps, AllocationDatum, AllocationSeries } from '../interface';
import { allocationChartData, lastSeriesKey } from '../utils';

const AXIS_TICK = { fill: 'hsl(var(--muted-foreground))', fontSize: 12 };
const GRID = 'hsl(var(--border))';
const SURFACE = 'hsl(var(--card))';
const BAR_THICKNESS = 20;

const truncate = (name: string) => (name.length > 18 ? `${name.slice(0, 17)}…` : name);

interface SegmentProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  dataKey?: string | number;
  payload?: AllocationDatum;
}

/**
 * One stacked segment. Segments are separated by a 2px gap in the surface color,
 * and only the end of the stack gets the 4px rounded corner.
 */
function makeSegment(series: AllocationSeries[]) {
  return function Segment({ x, y, width, height, fill, dataKey, payload }: SegmentProps) {
    const isEnd = payload !== undefined && lastSeriesKey(payload, series) === dataKey;
    return (
      <Rectangle
        x={x} y={y} width={width} height={height} fill={fill}
        stroke={SURFACE} strokeWidth={2}
        radius={isEnd ? [0, 4, 4, 0] : 0}
      />
    );
  };
}

interface TooltipProps {
  active?: boolean;
  payload?: { payload: AllocationDatum }[];
  series: AllocationSeries[];
}

function AllocationTooltip({ active, payload, series }: TooltipProps) {
  const datum = payload?.[0]?.payload;
  if (!active || !datum) return null;
  const rows = series.filter((s) => typeof datum[s.key] === 'number' && (datum[s.key] as number) > 0);
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      <p className="mb-1 font-medium">{datum.name}</p>
      {rows.length === 0 ? (
        <p className="text-muted-foreground">Nothing logged in this range.</p>
      ) : (
        <ul className="space-y-0.5">
          {rows.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5">
              <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
              <span>{s.name}</span>
              <span className="ml-auto pl-3 text-muted-foreground">
                {datum[s.key] as number}% · {datum.hours[s.key]}h
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1 border-t pt-1 text-muted-foreground">
        Total {datum.total ?? 0}% of {datum.availableHours}h available
      </p>
    </div>
  );
}

export function AllocationChart({ data, isLoading, isError }: AllocationChartProps) {
  const chart = useMemo(() => allocationChartData(data ?? []), [data]);
  const Segment = useMemo(() => makeSegment(chart.series), [chart.series]);
  const height = Math.min(900, Math.max(220, chart.data.length * 32 + 70));

  return (
    <ChartCard
      title="Allocation by employee"
      subtitle="Share of each person's capacity, split by project"
      isLoading={isLoading}
      isError={isError}
      isEmpty={!!data && data.length === 0}
      emptyMessage="No employees with available hours in this selection."
      chart={(
        <div>
          <div style={{ height }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart.data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
                <CartesianGrid horizontal={false} stroke={GRID} />
                <XAxis
                  type="number"
                  domain={[0, (max: number) => Math.max(100, Math.ceil(max / 10) * 10)]}
                  tickFormatter={(v: number) => `${v}%`}
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={{ stroke: GRID }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={130}
                  tickFormatter={truncate}
                  tick={AXIS_TICK}
                  tickLine={false}
                  axisLine={{ stroke: GRID }}
                />
                <Tooltip cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }} content={<AllocationTooltip series={chart.series} />} />
                {chart.series.map((s) => (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    stackId="allocation"
                    fill={s.color}
                    barSize={BAR_THICKNESS}
                    isAnimationActive={false}
                    shape={Segment}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
          <ChartLegend items={chart.series.map((s) => ({ color: s.color, label: s.name }))} />
        </div>
      )}
      table={(
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              {chart.series.map((s) => <TableHead key={s.key}>{s.name}</TableHead>)}
              <TableHead>Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {chart.data.map((d) => (
              <TableRow key={d.name}>
                <TableCell className="font-medium">{d.name}</TableCell>
                {chart.series.map((s) => (
                  <TableCell key={s.key}>
                    {typeof d[s.key] === 'number' ? `${d[s.key]}% (${d.hours[s.key]}h)` : '—'}
                  </TableCell>
                ))}
                <TableCell>{d.total ?? 0}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    />
  );
}
